import { useTheme as useNextTheme } from "next-themes";
import { useEffect, useState, useRef } from "react";
import { useAuth } from "./useAuth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

export type ThemeMode = "light" | "dark" | "auto";

const THEME_MODE_STORAGE_KEY = "myFitBeats_themeMode";

export function getTimeBasedTheme(): "light" | "dark" {
  const hour = new Date().getHours();
  return hour >= 6 && hour < 18 ? "light" : "dark";
}

export function getInitialTheme(): "light" | "dark" {
  if (typeof window === "undefined") return "dark";
  const storedMode = localStorage.getItem(THEME_MODE_STORAGE_KEY) as ThemeMode | null;
  if (storedMode === "light") return "light";
  if (storedMode === "dark") return "dark";
  return getTimeBasedTheme();
}

export function useThemeMode() {
  const { setTheme, resolvedTheme } = useNextTheme();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [mounted, setMounted] = useState(false);
  const hasInitializedFromUser = useRef(false);
  
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    if (typeof window === "undefined") return "auto";
    const stored = localStorage.getItem(THEME_MODE_STORAGE_KEY) as ThemeMode | null;
    if (stored && ["light", "dark", "auto"].includes(stored)) {
      return stored;
    }
    return "auto";
  });

  const updateThemeMutation = useMutation({
    mutationFn: async (mode: ThemeMode) => {
      const response = await apiRequest("PATCH", "/api/user/preferences", { themeMode: mode });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
    },
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (user && !hasInitializedFromUser.current) {
      const userTheme = (user as any)?.themeMode as ThemeMode | undefined;
      if (userTheme && ["light", "dark", "auto"].includes(userTheme)) {
        setThemeModeState(userTheme);
        localStorage.setItem(THEME_MODE_STORAGE_KEY, userTheme);
        hasInitializedFromUser.current = true;
      }
    }
    if (!user) {
      hasInitializedFromUser.current = false;
    }
  }, [user]);

  useEffect(() => {
    if (!mounted) return;
    
    const applyTheme = () => {
      if (themeMode === "auto") {
        setTheme(getTimeBasedTheme());
      } else {
        setTheme(themeMode);
      }
    };

    applyTheme();
    
    if (themeMode === "auto") {
      const interval = setInterval(applyTheme, 60000);
      return () => clearInterval(interval);
    }
  }, [themeMode, mounted, setTheme]);

  const updateThemeMode = (mode: ThemeMode) => {
    setThemeModeState(mode);
    localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
    if (mode === "auto") {
      setTheme(getTimeBasedTheme());
    } else {
      setTheme(mode);
    }
    if (user) {
      updateThemeMutation.mutate(mode);
    }
  };

  return {
    themeMode,
    setThemeMode: updateThemeMode,
    resolvedTheme: resolvedTheme as "light" | "dark" | undefined,
    mounted,
  };
}
